'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { PrudsysProductsDocument } from '@packages/gql/generated/shopping/PrudsysProductsDocument';
import type { FC } from 'react';
import { isValidElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCardV2.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';
import { ExtendedRecoCardItem } from '../../ExtendedRecoCardItem/ExtendedRecoCardItem.client';
import { ExtendedRecoLink } from '../../ExtendedRecoLink/ExtendedRecoLink.client';

export type PrudsysProductsRecoSliderProps = Pick<
  RequestParams,
  'productId' | 'brand' | 'categoryIds'
> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysProducts(
    $productId: String!
    $brand: String!
    $categoryIds: [String!]!
    $locale: String!
  ) {
    prudsysProducts(
      productId: $productId
      brand: $brand
      categoryIds: $categoryIds
      locale: $locale
    ) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
      extendedViewUrl
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysProducts';

/**
 * PrudsysProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysProductsRecoSlider: FC<PrudsysProductsRecoSliderProps> = ({
  backgroundColor,
  brand,
  categoryIds,
  elementIndex = 0,
  extendedSlot,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  productId,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysProductsDocument,
    variables: {
      productId,
      brand,
      categoryIds,
      locale,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  const extendedSlotElement = data?.prudsysProducts.extendedViewUrl
    ? extendedSlot?.(data?.prudsysProducts.extendedViewUrl)
    : undefined;

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysProducts.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
      slot={
        isValidElement(extendedSlotElement) &&
        extendedSlotElement.type === ExtendedRecoLink &&
        extendedSlotElement
      }
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          // pageTemplate={pageTemplate}
          // elementIndex={elementIndex}
          // position={idx}
          // productsLength={filteredRecommendations.length}
          // rdeType={RDE_TYPE}
        />
      ))}
      {isValidElement(extendedSlotElement) &&
        extendedSlotElement.type === ExtendedRecoCardItem &&
        extendedSlotElement}
    </RecoCarousel>
  );
};
